<script setup lang="ts">
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { MapPin, Tag, User } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'

import AddressForm, { type AddressFields } from '@/components/AddressForm.vue'
import InputError from '@/components/InputError.vue'
import NotificationSettings from '@/components/settings/NotificationSettings.vue'
import TokenList from '@/components/settings/TokenList.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatLabel } from '@/lib/utils'
import {
  portalPartnerNotificationPreferencePath,
  portalPartnerSettingsPath,
  portalPartnerTokenPath,
  portalPartnerTokensPath,
  portalPartnerWebhookSubscriptionPath,
  portalPartnerWebhookSubscriptionsPath,
  testPortalPartnerWebhookSubscriptionPath,
} from '@/routes'

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  website: string | null
  partner_type: string | null
  categories: string[] | null
}

interface SubscribableEvent {
  key: string
  label: string
}

interface WebhookDelivery {
  id: number
  status: string
  event: string | null
  response_status: number | null
  delivered_at: string | null
  created_at: string
  payload: Record<string, unknown> | string | null
}

interface WebhookSubscription {
  id: number
  url: string
  events: string
  active: boolean
  created_at: string
  last_delivery: WebhookDelivery | null
  recent_deliveries: WebhookDelivery[]
}

interface NotificationPreference {
  email_enabled: boolean
  email_events: string[]
}

interface Token {
  id: number
  name: string
  access: 'read' | 'read_write'
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

const props = defineProps<{
  partner: Partner
  address: AddressFields | null
  partner_categories: Record<string, string[]>
  partner_types: string[]
  notification_preference: NotificationPreference
  subscribable_events: SubscribableEvent[]
  webhook_subscriptions: WebhookSubscription[]
  tokens: Token[]
}>()

// --- Tab state (driven by ?tab= query param, set by the controllers on redirect) ---
const TABS = ['profile', 'notification', 'api-token'] as const
type SettingsTab = (typeof TABS)[number]

function tabFromUrl(): SettingsTab {
  const url = new URL(usePage().url, window.location.origin)
  const tab = url.searchParams.get('tab') as SettingsTab | null
  return tab && TABS.includes(tab) ? tab : 'profile'
}

const activeTab = ref<SettingsTab>(tabFromUrl())

watch(
  () => usePage().url,
  () => {
    activeTab.value = tabFromUrl()
  },
)

function switchTab(tab: string | number) {
  activeTab.value = tab as SettingsTab
  history.replaceState(null, '', `?tab=${tab}`)
}

// --- Profile form (includes Service Type + Address) ---
const profileForm = useForm({
  name: props.partner.name,
  contact: props.partner.contact,
  email: props.partner.email,
  phone: props.partner.phone,
  website: props.partner.website ?? '',
  partner_type: props.partner.partner_type ?? '',
  categories: (props.partner.categories ?? []) as string[],
  address_attributes: {
    unit_no: props.address?.unit_no ?? '',
    street_no: props.address?.street_no ?? '',
    street_name: props.address?.street_name ?? '',
    street_type: props.address?.street_type ?? '',
    city: props.address?.city ?? '',
    state: props.address?.state ?? '',
    postcode: props.address?.postcode ?? '',
  },
})

function saveProfile() {
  profileForm.patch(portalPartnerSettingsPath())
}

watch(
  () => profileForm.partner_type,
  (type) => {
    profileForm.categories = type === props.partner.partner_type ? [...(props.partner.categories ?? [])] : []
  },
)

function toggleCategory(cat: string, checked: boolean) {
  if (checked) {
    profileForm.categories = [...profileForm.categories, cat]
  } else {
    profileForm.categories = profileForm.categories.filter((c) => c !== cat)
  }
}

const availableCategories = computed(() => props.partner_categories[profileForm.partner_type] ?? [])
</script>

<template>
  <Head title="Settings" />
  <PortalLayout actor-type="partner">
    <div class="space-y-6">
      <h1 class="text-xl font-semibold">Settings</h1>

      <Tabs v-model="activeTab" @update:model-value="switchTab">
        <TabsList class="h-11 w-full">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notification">Notification</TabsTrigger>
          <TabsTrigger value="api-token">API Token</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" class="space-y-8">
          <!-- Profile & Service Type -->
          <Card>
            <CardHeader>
              <div class="flex items-center gap-2">
                <User class="text-muted-foreground size-4" />
                <CardTitle class="text-base">Profile</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form class="space-y-6" @submit.prevent="saveProfile">
                <div class="grid gap-4 sm:grid-cols-2">
                  <div class="grid gap-2">
                    <Label>Name</Label><Input v-model="profileForm.name" required />
                    <InputError :messages="profileForm.errors.name" />
                  </div>
                  <div class="grid gap-2"><Label>Contact</Label><Input v-model="profileForm.contact" required /></div>
                  <div class="grid gap-2">
                    <Label>Email</Label><Input v-model="profileForm.email" type="email" required />
                    <InputError :messages="profileForm.errors.email" />
                  </div>
                  <div class="grid gap-2">
                    <Label>Phone</Label><Input v-model="profileForm.phone" type="tel" required />
                  </div>
                  <div class="grid gap-2 sm:col-span-2">
                    <Label>Website</Label><Input v-model="profileForm.website" type="url" placeholder="https://" />
                  </div>
                </div>

                <div class="space-y-4 border-t pt-4">
                  <div class="flex items-center gap-2">
                    <Tag class="text-muted-foreground size-4" />
                    <p class="text-sm font-medium">Service Type</p>
                  </div>

                  <div class="grid gap-2">
                    <Label>Type</Label>
                    <Select v-model="profileForm.partner_type">
                      <SelectTrigger class="w-full sm:w-64">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="t in partner_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
                      </SelectContent>
                    </Select>
                    <InputError :messages="profileForm.errors.partner_type" />
                  </div>

                  <div v-if="availableCategories.length > 0" class="grid gap-2">
                    <Label>Categories</Label>
                    <p class="text-muted-foreground text-xs">Select all service categories you offer.</p>
                    <div class="grid grid-cols-2 gap-x-4 gap-y-2.5">
                      <div v-for="cat in availableCategories" :key="cat" class="flex items-center gap-2">
                        <Checkbox
                          :id="`cat-${cat}`"
                          :model-value="profileForm.categories.includes(cat)"
                          @update:model-value="toggleCategory(cat, $event as boolean)"
                        />
                        <Label :for="`cat-${cat}`" class="font-normal">{{ formatLabel(cat) }}</Label>
                      </div>
                    </div>
                    <InputError :messages="profileForm.errors.categories" />
                  </div>
                </div>

                <div class="space-y-4 border-t pt-4">
                  <div class="flex items-center gap-2">
                    <MapPin class="text-muted-foreground size-4" />
                    <p class="text-sm font-medium">Address</p>
                  </div>
                  <AddressForm
                    v-model="profileForm.address_attributes"
                    :errors="profileForm.errors"
                    error-prefix="address."
                  />
                </div>

                <div class="flex justify-end">
                  <Button type="submit" :disabled="profileForm.processing">Save Changes</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notification" class="space-y-8">
          <NotificationSettings
            :recipient-email="partner.email"
            :notification-preference="notification_preference"
            :subscribable-events="subscribable_events"
            :webhook-subscriptions="webhook_subscriptions"
            :preference-path="portalPartnerNotificationPreferencePath()"
            :webhook-create-path="portalPartnerWebhookSubscriptionsPath()"
            :webhook-path="(id) => portalPartnerWebhookSubscriptionPath(id)"
            :webhook-test-path="(id) => testPortalPartnerWebhookSubscriptionPath(id)"
          />
        </TabsContent>

        <TabsContent value="api-token">
          <TokenList
            :tokens="tokens"
            :create-path="portalPartnerTokensPath()"
            :destroy-path="(id) => portalPartnerTokenPath(id)"
          />
        </TabsContent>
      </Tabs>
    </div>
  </PortalLayout>
</template>
